/**
 * Utility functions for check-in operations
 */

/**
 * Load all comments for a specific goal from all non-archived checkins
 * @param {number} goalId - The ID of the goal
 * @param {Array} goals - Array of goals
 * @param {Array} checkIns - Array of check-ins
 * @param {Array} checkInGoals - Array of check-in goals
 * @param {Function} setComments - Function to set comments state
 * @param {Function} setCheckInGoalComments - Function to set check-in goal comments state
 * @param {Function} setGoalComments - Function to set goal comments state
 */
export const loadAllGoalComments = (
    goalId,
    goals,
    checkIns,
    checkInGoals,
    setComments,
    setCheckInGoalComments,
    setGoalComments
) => {
    try {
        // Clear previous comments
        setComments([])
        setCheckInGoalComments([])
        setGoalComments([])

        // Use pullstate data
        if (!checkIns || !checkInGoals) {
            return
        }

        // First, check if there are comments in local state for this goal
        const localGoal = goals.find(goal => goal.goal_id === parseInt(goalId))
        if (localGoal && localGoal.goal_comments && Array.isArray(localGoal.goal_comments)) {
            // If there are comments in local state, use them
            const sortedComments = localGoal.goal_comments.sort((a, b) => new Date(a.commentDate) - new Date(b.commentDate))
            setComments(sortedComments)
            setCheckInGoalComments(sortedComments)
            return
        }

        const validCheckInsIds = checkIns.filter(checkIn => checkIn.status !== 'archived').map(checkIn => checkIn.id)

        // Filter checkin_goals for this specific goal
        const relevantCheckInGoals = checkInGoals.filter(goal =>
            goal.goal_id === parseInt(goalId) &&
            validCheckInsIds.includes(goal.check_in_id)
        )

        // Comments ONLY from checkin_goals (don't include goal form comments)
        const allComments = relevantCheckInGoals
            .filter(goal =>
                goal.goal_comments &&
                goal.goal_comments !== '[]' &&
                goal.goal_comments !== 'null' &&
                goal.goal_comments.trim() !== ''
            )
            .map((goal, goalIndex) => {
                try {
                    const parsed = JSON.parse(goal.goal_comments)

                    // Ensure we only get comments that have expected checkin comment properties
                    const filteredComments = Array.isArray(parsed) ? parsed.filter(comment =>
                        comment &&
                        typeof comment === 'object' &&
                        comment.comment &&
                        comment.commentDate &&
                        comment.name
                    ) : []

                    return filteredComments
                } catch (e) {
                    console.error('Error parsing comments:', e)
                    return []
                }
            })
            .flat()

        // Remove duplicates based on commentDate + comment + name
        const uniqueComments = allComments.filter((comment, index, self) =>
            index === self.findIndex((c) =>
                c.commentDate === comment.commentDate &&
                c.comment === comment.comment &&
                c.name === comment.name
            )
        )

        const sortedComments = uniqueComments.sort((a, b) => new Date(a.commentDate) - new Date(b.commentDate))

        setComments(sortedComments)
        setCheckInGoalComments(sortedComments)

    } catch (error) {
        console.error('Error loading goal comments:', error)
    }
}

/**
 * Check if a goal has comments in any checkin
 * @param {number} goalId - The ID of the goal
 * @param {Array} goals - Array of goals
 * @param {Array} checkIns - Array of check-ins
 * @param {Array} checkInGoals - Array of check-in goals
 * @returns {boolean} - True if goal has comments, false otherwise
 */
export const checkHasAnyComments = (goalId, goals, checkIns, checkInGoals) => {
    try {
        if (!checkIns || !checkInGoals) {
            return false
        }

        // First, check if there are comments in local state of goals (updated immediately)
        const localGoal = goals.find(goal => goal.goal_id === parseInt(goalId))
        if (localGoal && localGoal.goal_comments && Array.isArray(localGoal.goal_comments) && localGoal.goal_comments.length > 0) {
            return true
        }

        // If not found in local state, check in pullstate
        const validCheckInsIds = checkIns
            .filter(checkIn => checkIn.status !== 'archived')
            .map(checkIn => checkIn.id)

        return checkInGoals.some(goal => {
            const hasGoalId = goal.goal_id === parseInt(goalId)
            const hasValidCheckIn = validCheckInsIds.includes(goal.check_in_id)
            const hasComments = goal.goal_comments &&
                goal.goal_comments !== '[]' &&
                goal.goal_comments !== 'null' &&
                goal.goal_comments.trim() !== ''

            if (hasGoalId && hasValidCheckIn && hasComments) {
                try {
                    const parsed = JSON.parse(goal.goal_comments)
                    // Check if it's an array with valid comments (checkin comments structure)
                    if (Array.isArray(parsed)) {
                        return parsed.some(comment =>
                            comment &&
                            comment.comment &&
                            comment.commentDate &&
                            comment.name
                        )
                    }
                    return false
                } catch (e) {
                    return false
                }
            }
            return false
        })
    } catch (error) {
        console.error('Error checking comments:', error)
        return false
    }
}

/**
 * Handle status change for a goal
 * @param {number} id - The ID of the goal
 * @param {string} value - The new status value
 * @param {Array} goals - Array of goals
 * @param {Function} setGoals - Function to set goals state
 * @param {Function} setCheckinGoalData - Function to set checkin goal data
 * @param {Function} setEditingId - Function to set editing ID
 */
export const handleStatusChange = (id, value, goals, setGoals, setCheckinGoalData, setEditingId) => {
    const newGoalsData = []
    goals.map((goal) => {
        if (goal.id === id) {
            goal.check_in_goal_status = value
        }
        newGoalsData.push(goal)
    })
    setEditingId(null)
    setGoals(newGoalsData)
    setCheckinGoalData((prevStates) => prevStates.map((goal) => goal.id === id ? {
        ...goal,
        check_in_goal_status: value
    } : goal))
}

/**
 * Handle comments change for a goal
 * @param {number} id - The ID of the goal
 * @param {Array} value - The new comments value
 * @param {Array} goals - Array of goals
 * @param {Function} setGoals - Function to set goals state
 * @param {Function} setCheckinGoalData - Function to set checkin goal data
 * @param {Function} setEditingId - Function to set editing ID
 * @param {Function} setComments - Function to set comments state
 * @param {Function} setCheckInGoalComments - Function to set check-in goal comments state
 */
export const handleCommentsChange = (
    id,
    value,
    goals,
    setGoals,
    setCheckinGoalData,
    setEditingId,
    setComments,
    setCheckInGoalComments
) => {
    const newGoalsData = []
    goals.map((goal) => {
        if (goal.id === id) {
            goal.goal_comments = value
        }
        newGoalsData.push(goal)
    })
    setEditingId(null)
    setGoals(newGoalsData)
    setCheckinGoalData((prevStates) => prevStates.map((goal) => {
        return goal.id === id ? {
            ...goal,
            goal_comments: JSON.stringify(value)
        } : goal
    }))

    // Update comments locally as well
    setComments(value)
    setCheckInGoalComments(value)
}

/**
 * Transform goal data for display in DataGrid
 * @param {Array} dataFiltered - Filtered goals data
 * @returns {Array} - Transformed goal data
 */
export const transformGoalData = (dataFiltered) => {
    const goalData = []
    dataFiltered.map((goal) => {
        goalData.push({
            id: goal.id,
            goal_id: goal.id,
            goalTitle: goal.goal_name,
            goalType: goal.goal_term,
            lastCheckinDate: goal.last_check_in?.split('T')[0],
            check_in_goal_status: goal.checkin_status,
            goal_comments: goal.goal_comments,
            goal_status: goal.goal_status,
            people_builder_id: goal.people_builder_id
        })
    })
    return goalData
}

/**
 * Transform checkin goal data for display in DataGrid
 * @param {Array} checkinFiltered - Filtered checkin goals data
 * @param {number} peopleBuilderId - People builder ID to filter by
 * @returns {Array} - Transformed checkin goal data
 */
export const transformCheckinGoalData = (checkinFiltered, peopleBuilderId) => {
    const goalData = []
    const filtered = checkinFiltered?.filter((goal) => goal?.goal?.people_builder_id === parseInt(peopleBuilderId))

    filtered?.map((goal) => {
        goalData.push({
            id: goal.id,
            goal_id: goal.goal_id,
            goalTitle: goal.goal.goal_name,
            goalType: goal.goal.goal_term,
            lastCheckinDate: goal?.goal?.last_check_in?.split('T')[0],
            check_in_goal_status: goal.check_in_goal_status,
            goal_comments: goal.goal_comments,
            goal_status: goal.goal.goal_status,
            last_check_in: goal.goal.last_check_in,
            people_builder_id: goal.goal.people_builder_id
        })
    })
    return goalData
}
